import { useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { apiFetch } from '../../../lib/api-client';
import { API_BASE } from '../../../lib/api-base';
import { refreshOutreaches } from '../../../lib/outreaches';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { TextButton } from '../ui';
import styles from './outreach-thread.module.css';

/**
 * The way out of a message that needs nothing from you.
 *
 * Every surface files an inbound outreach under "waiting on you" from the SHAPE of the trail — the
 * thread is not COMPLETED and they spoke last. That is a proxy for "they asked you something", and the
 * two part company on the commonest reply of all: an answer to a question the USER asked. The receiving
 * agent is meant to judge the message on arrival and finish the thread itself, and when it does this
 * button is not here to be pressed. When it doesn't, there is no second chance — the user has nothing to
 * say, so nothing wakes the agent again — and until this shipped the ONLY way to clear the row was to
 * reply to it. Sending a message you did not want to send, to make a badge go away, is not a way out.
 *
 * So this is the same judgement, made by the reader, writing the same COMPLETED status the agent's own
 * `complete` writes. Nothing is optimistic: the row changes only after the write lands, because the
 * whole complaint here is a surface claiming a state that is not true.
 *
 * It is deliberately NOT destructive and asks for no confirmation. COMPLETED means "no work pending",
 * not "closed forever": if they write again the new message reopens the thread, so the worst a stray
 * press can do is hide a row until the next message arrives.
 */
export function OutreachNoReplyNeeded({ outreachId, onCompleted }: {
  outreachId: string;
  /** Fired once the thread is finished, so the dialog can close itself. */
  onCompleted: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const complete = async () => {
    if (busy) return;
    logPageClickBi({ ...TASKS_PAGE, button_name: 'outreach_no_reply_needed' });
    setBusy(true);
    setFailed(false);
    try {
      const res = await apiFetch(`${API_BASE}/api/outreach-complete`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ outreachId }),
      });
      if (!res.ok) throw new Error(`complete ${res.status}`);
      // Re-read before closing, so the list the user lands back on already shows the row settled rather
      // than still red until the next ambient refresh.
      await refreshOutreaches().catch(() => undefined);
      onCompleted();
    } catch {
      setFailed(true);
      setBusy(false);
    }
  };

  return (
    <div className={styles.noReplyRow}>
      {/* Announced, because the only other evidence of a failure is a dialog that stayed open — which
          reads as a button that does nothing. ABOVE the button, where both phones put it: a reader who
          presses again should have read why the first press did not take. */}
      {failed ? (
        <p className={styles.failed} role="alert">
          {t('tasks.outreach.noReplyNeeded.failed')}
        </p>
      ) : null}
      <TextButton size="small" onClick={complete} disabled={busy}>
        {t('tasks.outreach.noReplyNeeded')}
      </TextButton>
    </div>
  );
}
